import { useMemo, useId } from "react";
import { ChevronDown, Eye } from "lucide-react";
import { SERIES_COLORS } from "@/constants/chart-constants.js";
import { rankRemainingTrends } from "@/components/root-cause-analysis/rca-key-trend-score.js";
import RcaMeasurementFilter from "@/components/root-cause-analysis/rca-measurement-filter.jsx";
import RcaMeasurementLabelHighlight from "@/components/root-cause-analysis/rca-measurement-label-highlight.jsx";
import { sortKeyedTrendEntries } from "@/components/root-cause-analysis/rca-measurement-sort.js";
import RcaMeasurementSortToolbar from "@/components/root-cause-analysis/rca-measurement-sort-toolbar.jsx";
import {
  getMeasurementLabel,
  measurementLabelMatchesFilters,
} from "@/components/root-cause-analysis/root-cause-analysis-helpers.js";
import { useControllableState } from "@/components/root-cause-analysis/use-controllable-state.js";
import {
  RCA_TREND_CARD_CLASS,
  RCA_TREND_LIST_CLASS,
  RcaTrendCardRow,
} from "@/components/root-cause-analysis/rca-measurement-trend-shared.jsx";
import { cn } from "@/lib/utils.js";

const REMAINING_TREND_LIST_CLASS = cn(RCA_TREND_LIST_CLASS, "mt-2");

/**
 * Collapsible list of sensors that are not key trends (score strictly below threshold).
 *
 * @param {{
 *   rows: unknown[],
 *   overallFilterText?: string,
 *   filterText?: string,
 *   onFilterTextChange?: (value: string) => void,
 *   zScoreThreshold: number,
 *   keyTrendScoreThreshold: number,
 *   useAlarmLimits?: boolean,
 *   sortMode?: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode,
 *   onSortModeChange?: (mode: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode) => void,
 *   sortDirection?: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection,
 *   onSortDirectionChange?: (direction: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection) => void,
 *   expanded?: boolean,
 *   onExpandedChange?: (expanded: boolean) => void,
 * }} props
 * @returns {JSX.Element|null}
 */
export default function RemainingSensorsCollapsible({
  rows,
  overallFilterText = "",
  filterText: filterTextProp,
  onFilterTextChange,
  zScoreThreshold,
  keyTrendScoreThreshold,
  useAlarmLimits = true,
  sortMode: sortModeProp,
  onSortModeChange,
  sortDirection: sortDirectionProp,
  onSortDirectionChange,
  expanded: expandedProp,
  onExpandedChange,
}) {
  const [expanded, setExpanded] = useControllableState(
    expandedProp,
    onExpandedChange,
    false
  );
  const [filterText, setFilterText] = useControllableState(
    filterTextProp,
    onFilterTextChange,
    ""
  );
  const [sortMode, setSortMode] = useControllableState(
    sortModeProp,
    onSortModeChange,
    /** @type {import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode} */ (
      "score"
    )
  );
  const [sortDirection, setSortDirection] = useControllableState(
    sortDirectionProp,
    onSortDirectionChange,
    /** @type {import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection} */ (
      "desc"
    )
  );
  const headingId = useId();

  function toggleExpanded() {
    setExpanded(!expanded);
  }

  const scoreOptions = useMemo(
    () => ({
      useAlarmLimits,
      zScoreThreshold,
      keyTrendScoreThreshold,
    }),
    [useAlarmLimits, zScoreThreshold, keyTrendScoreThreshold]
  );

  const allRemaining = useMemo(
    () => rankRemainingTrends(rows, scoreOptions),
    [rows, scoreOptions]
  );

  const remaining = useMemo(
    () => {
      const filtered = allRemaining.filter(({ row }) => {
        const label = getMeasurementLabel(row);
        return measurementLabelMatchesFilters(label, {
          sectionFilterText: filterText,
          overallFilterText,
        });
      });

      return sortKeyedTrendEntries(filtered, { sortMode, sortDirection });
    },
    [allRemaining, sortMode, sortDirection, filterText, overallFilterText]
  );
  const filteredCount = remaining.length;
  const totalCount = allRemaining.length;

  if (!Array.isArray(rows) || rows.length === 0 || totalCount === 0) {
    return null;
  }

  return (
    <section className='rounded-lg border border-border bg-muted/60 px-2 py-2 sm:px-3'>
      <button
        type='button'
        className='flex w-full items-start gap-3 rounded-md text-left transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60'
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-controls={expanded ? `${headingId}-panel` : undefined}
        id={headingId}>
        <Eye className='mt-0.5 h-5 w-5 shrink-0 text-muted-foreground' aria-hidden />
        <div className='min-w-0 flex-1'>
          <h3 className='text-sm font-semibold text-foreground'>
            Show Remaining Sensors ({filteredCount} / {totalCount})
          </h3>
          <p className='mt-1 text-xs text-muted-foreground'>
            {`Sensors with key-trend score < ${keyTrendScoreThreshold} (and plottable data).`}
          </p>
        </div>
        <ChevronDown
          className={cn(
            "ml-auto h-7 w-7 shrink-0 self-center text-muted-foreground transition-transform",
            expanded && "rotate-180"
          )}
          aria-hidden
        />
      </button>

      {expanded ? (
        <>
          <div className='mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-border/80 pt-2'>
            <RcaMeasurementFilter
              filterText={filterText}
              onFilterTextChange={setFilterText}
            />
            <RcaMeasurementSortToolbar
              sortMode={sortMode}
              onSortModeChange={setSortMode}
              sortDirection={sortDirection}
              onSortDirectionChange={setSortDirection}
            />
          </div>
          <ul
            id={`${headingId}-panel`}
            className={REMAINING_TREND_LIST_CLASS}
            aria-labelledby={headingId}>
          {filteredCount === 0 ? (
            <li className='text-xs text-muted-foreground'>
              No measurements match the current filter.
            </li>
          ) : (
            remaining.map(({ row, score }, index) => {
            const lineColor =
              SERIES_COLORS[index % SERIES_COLORS.length] ?? "#0ea5e9";
            const label = getMeasurementLabel(row);
            const highlightedLabel = (
              <RcaMeasurementLabelHighlight
                label={label}
                filterText={filterText.trim() || overallFilterText}
              />
            );
            const uom = String(row.UoM ?? "").trim();

            return (
              <li
                key={row.AssetTagId ?? index}
                className={RCA_TREND_CARD_CLASS}>
                <RcaTrendCardRow
                  row={row}
                  lineColor={lineColor}
                  label={highlightedLabel}
                  uom={uom}
                  useAlarmLimits={useAlarmLimits}
                  score={score}
                />
              </li>
            );
          })
          )}
          </ul>
        </>
      ) : null}
    </section>
  );
}
